import { 
  AssistReceiptItem
} from '../features/daily-report/types';
import { 
  Employee
} from '../contexts/AppContext';
import { getMonthlyReceiptDataByTeam, getMonthlyExpenseDataByTeam } from './firebase/monthlyDataFirebaseService.ts';

// ========================================
// 타입 정의
// ========================================
interface DailyData {
  [key: string]: any;
}

interface TeamCollection {
  [teamName: string]: {
    team: string;
    dailyCount: number;
    dailyAmount: number;
    dailyCash: number;
    dailyCard: number;
    dailyCashCount: number;
    dailyCardCount: number;
    assistantStats: any;
    doctorStats: any;
    expenseTotal: number;
    receiptTotal: number;
    totalAmount: number;
    receiptItems: AssistReceiptItem[];
    monthlyReceipt: number;
    monthlyExpense: number;
    monthlyProfit: number;
  };
}

interface TeamSummary {
  [teamName: string]: {
    count: number;
    amount: number;
    cashCount: number;
    cashAmount: number;
    cardCount: number;
    cardAmount: number;
    assistNames: string[];
    monthlyReceiptTotal: number;
    monthlyExpenseTotal: number;
  };
}

type AssistCashierDoctorData = Record<string, {
  matchedDoctor?: string;
  [key: string]: any;
}>;

// 설정 상수
const TEAM_MAPPING_CONFIG = {
  useNewMapping: true,
  enableCashierFallback: true,
  enableDoctorMapping: true,
  minNameLength: 2,
  maxRetryDays: 30
} as const;

// 타입 정의 강화
interface EmployeeMatchResult {
  employee: Employee | null;
  matchType: 'exact' | 'whitespace' | 'partial' | 'surname' | 'none';
  confidence: number;
}

interface MonthlyDataResult {
  receipt: Record<string, any>;
  expense: Record<string, any>;
  isFromCache: boolean;
}

interface TeamCollectionInput {
  dateStr?: string;
  assistItems: AssistReceiptItem[];
  employees: Employee[];
  budgetData?: any;
  teamBudgets?: any;
  dailyReceiptData?: DailyData;
  dailyExpenseData?: DailyData;
  monthlyReceiptData?: any;
  monthlyExpenseData?: any;
  assistCashierDoctorData?: AssistCashierDoctorData;
  yearMonth?: string; // 🆕 진료의사 매핑을 위한 년월 정보 추가
}

interface TeamCollectionOutput {
  teamCollection: TeamCollection;
  teamSummary: TeamSummary;
}

export class TeamCollectionService {
  private assistReceiptCache: Record<string, number> = {};
  private teamReceiptCache: Record<string, number> = {};
  private monthlyDataCache: Map<string, { receipt: any; expense: any; timestamp: number }> = new Map();
  private employeeMatchCache: Map<string, EmployeeMatchResult> = new Map();
  
  // 성능 향상을 위한 추가 캐시
  private monthlyAssistCache: Map<string, Record<string, number>> = new Map();
  private monthlyExpenseCache: Map<string, Record<string, number>> = new Map();
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2분 캐시 (financial transactions 즉시 반영 위해 단축)
  
  // 🆕 일별 팀 활동 추적 캐시 (날짜_어시스트명_팀명 형태)
  private dailyTeamActivityCache: Map<string, Map<string, Set<string>>> = new Map();
  
  // 성능 통계
  private performanceStats = {
    cacheHits: 0,
    cacheMisses: 0,
    totalRequests: 0
  };

  /**
   * 화면 표시용 팀별 수납 현황 계산 (TeamCollectionReport 기준)
   */
  async calculateForDisplay(input: TeamCollectionInput): Promise<TeamCollectionOutput> {
    const { 
      assistItems, 
      employees, 
      budgetData,
      teamBudgets,
      assistCashierDoctorData,
      monthlyReceiptData,
      monthlyExpenseData
    } = input;

    // 팀별 데이터 초기화
    const teamData: Record<string, {
      count: number;
      amount: number;
      cashCount: number;
      cashAmount: number;
      cardCount: number;
      cardAmount: number;
      assistNames: Set<string>;
      monthlyReceiptTotal: number;
      monthlyExpenseTotal: number;
      receiptItems: AssistReceiptItem[];
    }> = {};

    // 모든 팀 초기화
    const allTeams = new Set<string>();
    employees.forEach(emp => {
      if (emp.team) allTeams.add(emp.team);
    });
    if (budgetData?.teams) {
      Object.keys(budgetData.teams).forEach(team => allTeams.add(team));
    }
    if (teamBudgets) {
      Object.keys(teamBudgets).forEach(team => allTeams.add(team));
    }
    
    // 🔧 미지정 팀 명시적 추가 (누락된 어시스트 데이터 처리용)
    allTeams.add('미지정');

    allTeams.forEach(teamName => {
      teamData[teamName] = {
        count: 0,
        amount: 0,
        cashCount: 0,
        cashAmount: 0,
        cardCount: 0,
        cardAmount: 0,
        assistNames: new Set(),
        monthlyReceiptTotal: 0,
        monthlyExpenseTotal: 0,
        receiptItems: []
      };
    });

    // 팀 매핑 및 집계
    for (const item of assistItems) {
      const teamName = await this.mapItemToTeam(item, employees, assistCashierDoctorData, input.budgetData, input.yearMonth);
      
      if (!teamData[teamName]) {
        teamData[teamName] = {
          count: 0,
          amount: 0,
          cashCount: 0,
          cashAmount: 0,
          cardCount: 0,
          cardAmount: 0,
          assistNames: new Set(),
          monthlyReceiptTotal: 0,
          monthlyExpenseTotal: 0,
          receiptItems: []
        };
      }

      // 데이터 집계
      teamData[teamName].count += item.count || 0;
      teamData[teamName].amount += item.amount || 0;
      teamData[teamName].receiptItems.push(item);
      
      if (item.assistName) {
        teamData[teamName].assistNames.add(item.assistName);
      }

      // 결제 방식별 집계 (isCash 속성이 있는 경우에만)
      if ('isCash' in item && item.isCash) {
        teamData[teamName].cashCount += item.count || 0;
        teamData[teamName].cashAmount += item.amount || 0;
      } else {
        teamData[teamName].cardCount += item.count || 0;
        teamData[teamName].cardAmount += item.amount || 0;
      }
    }

    // 🆕 월별 데이터 처리 로직 - 수납자 매핑 결과 반영
    if (monthlyReceiptData && monthlyExpenseData) {
      // 1단계: 수납자 매핑 결과를 통해 어시스트별 팀 분배 비율 계산
      const assistTeamMappingRatio: Record<string, Record<string, number>> = {};
      
      for (const item of assistItems) {
        const assistName = item.assistName;
        const teamName = await this.mapItemToTeam(item, employees, input.assistCashierDoctorData, input.budgetData, input.yearMonth);
        
        if (!assistTeamMappingRatio[assistName]) {
          assistTeamMappingRatio[assistName] = {};
        }
        
        const amount = item.amount || 0;
        assistTeamMappingRatio[assistName][teamName] = 
          (assistTeamMappingRatio[assistName][teamName] || 0) + amount;
        
      }
      
      // 2단계: 비율을 계산하여 월별 데이터 분배
      Object.entries(assistTeamMappingRatio).forEach(([assistName, teamAmounts]) => {
        const totalDailyAmount = Object.values(teamAmounts).reduce((sum, amount) => sum + amount, 0);
        const monthlyReceipt = monthlyReceiptData[assistName] || 0;
        const monthlyExpense = monthlyExpenseData[assistName] || 0;
        
        if (totalDailyAmount > 0) {
          // 일별 활동 비율에 따라 월별 데이터 분배
          Object.entries(teamAmounts).forEach(([teamName, dailyAmount]) => {
            const ratio = dailyAmount / totalDailyAmount;
            const allocatedReceipt = monthlyReceipt * ratio;
            const allocatedExpense = monthlyExpense * ratio;

            if (teamData[teamName]) {
              teamData[teamName].monthlyReceiptTotal =
                (teamData[teamName].monthlyReceiptTotal || 0) + allocatedReceipt;
              teamData[teamName].monthlyExpenseTotal =
                (teamData[teamName].monthlyExpenseTotal || 0) + allocatedExpense;

            }
          });
        } else if (monthlyReceipt > 0) {
          // 일별 활동이 없지만 월별 데이터가 있는 경우, 직원 정보 기반 분배
          const matchedEmployee = this.findMatchingEmployeeWithCashier(assistName, undefined, employees);
          const defaultTeam = matchedEmployee?.team || '미지정';
          
          if (teamData[defaultTeam]) {
            teamData[defaultTeam].monthlyReceiptTotal = 
              (teamData[defaultTeam].monthlyReceiptTotal || 0) + monthlyReceipt;
            teamData[defaultTeam].monthlyExpenseTotal = 
              (teamData[defaultTeam].monthlyExpenseTotal || 0) + monthlyExpense;
            
          } else {
            // 🔧 팀이 초기화되지 않은 경우 동적 생성 (미지정 팀 등)
            teamData[defaultTeam] = {
              count: 0,
              amount: 0,
              cashCount: 0,
              cashAmount: 0,
              cardCount: 0,
              cardAmount: 0,
              assistNames: new Set(),
              monthlyReceiptTotal: monthlyReceipt,
              monthlyExpenseTotal: monthlyExpense,
              receiptItems: []
            };
          }
        }
      });
      
    }

    // TeamCollection 및 TeamSummary 변환
    const teamCollection: TeamCollection = {};
    const teamSummary: TeamSummary = {};

    Object.entries(teamData).forEach(([teamName, data]: [string, any]) => {
      teamCollection[teamName] = {
        team: teamName,
        dailyCount: data.count,
        dailyAmount: data.amount,
        dailyCash: data.cashAmount,
        dailyCard: data.cardAmount,
        dailyCashCount: data.cashCount,
        dailyCardCount: data.cardCount,
        assistantStats: {},
        doctorStats: {},
        expenseTotal: data.monthlyExpenseTotal || 0,
        receiptTotal: data.monthlyReceiptTotal || 0,
        totalAmount: data.amount,
        receiptItems: data.receiptItems,
        // 월별 데이터 필드 추가
        monthlyReceipt: data.monthlyReceiptTotal || 0,
        monthlyExpense: data.monthlyExpenseTotal || 0,
        monthlyProfit: (data.monthlyReceiptTotal || 0) - (data.monthlyExpenseTotal || 0)
      };

      teamSummary[teamName] = {
        count: data.count,
        amount: data.amount,
        cashCount: data.cashCount,
        cashAmount: data.cashAmount,
        cardCount: data.cardCount,
        cardAmount: data.cardAmount,
        assistNames: Array.from(data.assistNames),
        monthlyReceiptTotal: data.monthlyReceiptTotal,
        monthlyExpenseTotal: data.monthlyExpenseTotal
      };
    });

    return { teamCollection, teamSummary };
  }

  /**
   * Excel 업로드용 팀별 수납 현황 계산 (최적화)
   */
  async calculateForExcelUpload(
    dateStr: string,
    assistItems: AssistReceiptItem[],
    employees: Employee[],
    budgetData: any
  ): Promise<Record<string, any>> {
    const yearMonth = dateStr.substring(0, 7);
    const startTime = performance.now();
    
    // 병렬 처리로 성능 향상
    // employees 전달: budgetData 기반 직원 목록으로 Firebase에 없는 직원도 팀 매핑 가능
    const [monthlyData] = await Promise.all([
      this.loadMonthlyData(yearMonth, employees),
      // 직원 매칭도 백그라운드에서 미리 처리
      this.preProcessEmployeeMatching(assistItems, employees)
    ]);
    
    // 사전 계산된 캐시 활용
    this.calculateAssistReceiptCache(monthlyData.receipt, yearMonth);
    const assistExpenseCache = this.calculateAssistExpenseCache(monthlyData.expense, yearMonth);

    const teamCollectionMap: Record<string, any> = {};

    // 팀별 초기화
    if (budgetData?.teams) {
      Object.keys(budgetData.teams).forEach(teamName => {
        teamCollectionMap[teamName] = {
          team: teamName,
          dailyCount: 0,
          dailyAmount: 0,
          dailyCash: 0,
          dailyCard: 0,
          assistantStats: {},
          expenseTotal: 0,
          receiptTotal: 0
        };
      });
    }

    // 벌크 처리로 성능 향상
    const itemsByTeam = this.groupItemsByTeam(assistItems, employees);
      
    // 팀별 일괄 집계
    Object.entries(itemsByTeam).forEach(([teamName, items]) => {
      if (!teamCollectionMap[teamName]) {
        teamCollectionMap[teamName] = {
          team: teamName,
          dailyCount: 0,
          dailyAmount: 0,
          dailyCash: 0,
          dailyCard: 0,
          assistantStats: {},
          expenseTotal: 0,
          receiptTotal: 0
        };
      }

      // 팀별 일괄 집계
      this.aggregateTeamData(teamCollectionMap[teamName], items);
    });

    // 월별 데이터 추가 (최적화된 방식)
    this.addOptimizedMonthlyData(teamCollectionMap, assistExpenseCache, employees);

    return teamCollectionMap;
  }

  /**
   * Excel 업로드용 배열 형태 팀별 수납 현황 계산 (화면 표시와 동일한 형태)
   */
  async calculateForExcelUploadAsArray(
    dateStr: string,
    assistItems: AssistReceiptItem[],
    employees: Employee[],
    budgetData: any,
    monthlyTotalCache: Record<string, number> = {},
    monthlyExpenseCache: Record<string, number> = {}
  ): Promise<any[]> {
    const yearMonth = dateStr.substring(0, 7);
    try {
      // 월별 데이터 로드
      // employees 전달: budgetData 기반 직원 목록으로 Firebase에 없는 직원도 팀 매핑 가능
      const monthlyData = await this.loadMonthlyData(yearMonth, employees);

      // 어시스트별 캐시 계산
      this.calculateAssistReceiptCache(monthlyData.receipt, yearMonth);
      const assistExpenseCache = this.calculateAssistExpenseCache(monthlyData.expense, yearMonth);
      
      // 캐시가 비어있으면 월별 데이터에서 생성
      if (Object.keys(monthlyTotalCache).length === 0) {
        Object.entries(this.assistReceiptCache).forEach(([assistName, amount]) => {
          monthlyTotalCache[assistName] = amount;
        });
      }
      
      if (Object.keys(monthlyExpenseCache).length === 0) {
        Object.entries(assistExpenseCache).forEach(([assistName, amount]) => {
          monthlyExpenseCache[assistName] = amount;
        });
      }

      // 화면 표시용 계산 로직 사용
      const { teamCollection, teamSummary } = await this.calculateForDisplay({
        assistItems: assistItems,
        employees: employees,
        budgetData: budgetData,
        monthlyReceiptData: monthlyTotalCache,
        monthlyExpenseData: monthlyExpenseCache
      });

      // 배열 형태 데이터 생성 - 화면 UI와 동일하게 어시스트별 개별 행 생성
      const data: any[] = [];
      let totalReceipt = 0;
      let totalExpense = 0;
      const processedAssists = new Set<string>();

      // 팀별로 데이터 생성
      Object.keys(teamCollection).sort().forEach(teamName => {
        const summary = teamSummary[teamName];
        if (!summary) return;

        // 팀 헤더 행 추가
        // 팀별 헤더 행 생성 코드 제거됨

        let teamMonthlyReceipt = 0;
        let teamMonthlyExpense = 0;

        // 팀 어시스트들 수집
        const teamAssistants = new Map<string, {count: number, amount: number}>();
        const teamItems = assistItems.filter(item => {
          const mappedTeam = this.findMatchingEmployee(item.assistName, employees)?.team;
          return mappedTeam === teamName;
        });

        teamItems.forEach(item => {
          if (teamAssistants.has(item.assistName)) {
            const existing = teamAssistants.get(item.assistName)!;
            existing.count += item.count;
            existing.amount += item.amount;
          } else {
            teamAssistants.set(item.assistName, {count: item.count, amount: item.amount});
          }
        });

        // 각 어시스트별로 개별 행 생성
        teamAssistants.forEach((assistData, assistName) => {
          const assistMonthlyReceipt = monthlyTotalCache[assistName] || 0;
          const assistMonthlyExpense = monthlyExpenseCache[assistName] || 0;
          const matchedEmployee = this.findMatchingEmployee(assistName, employees);
          const isUnassignedEmployee = !matchedEmployee && assistMonthlyReceipt > 0;
          
          // 어시스트 개별 행 추가
          data.push({
            team: teamName,
            teamName: '',  // 어시스트 행에서는 팀명 비워둠
            assistName: assistName,
            count: assistData.count,
            amount: assistData.amount,
            monthlyReceipt: assistMonthlyReceipt,
            monthlyExpense: assistMonthlyExpense,
            monthlyProfit: assistMonthlyReceipt - assistMonthlyExpense,
            receiptTotal: assistMonthlyReceipt,
            expenseTotal: assistMonthlyExpense,
            isAssistRow: true,
            isUnassignedEmployee: isUnassignedEmployee
          });
          
          teamMonthlyReceipt += assistMonthlyReceipt;
          teamMonthlyExpense += assistMonthlyExpense;
          processedAssists.add(assistName);
        });

        // 추가 어시스트 확인 (월별 데이터만 있는 경우)
        const additionalAssists = Object.keys(monthlyTotalCache).filter(assistName => 
          !teamAssistants.has(assistName) && !processedAssists.has(assistName)
        );

        additionalAssists.forEach(assistName => {
          const matchedEmployee = this.findMatchingEmployee(assistName, employees);
          if (matchedEmployee?.team === teamName) {
            const assistMonthlyReceipt = monthlyTotalCache[assistName] || 0;
            const assistMonthlyExpense = monthlyExpenseCache[assistName] || 0;
            
            // 추가 어시스트 개별 행 추가
            data.push({
              team: teamName,
              teamName: '',
              assistName: assistName,
              count: 0,
              amount: 0,
              monthlyReceipt: assistMonthlyReceipt,
              monthlyExpense: assistMonthlyExpense,
              monthlyProfit: assistMonthlyReceipt - assistMonthlyExpense,
              receiptTotal: assistMonthlyReceipt,
              expenseTotal: assistMonthlyExpense,
              isAssistRow: true,
              isUnassignedEmployee: false
            });
            
            teamMonthlyReceipt += assistMonthlyReceipt;
            teamMonthlyExpense += assistMonthlyExpense;
            processedAssists.add(assistName);
          }
        });

        // 팀 헤더 행 업데이트 코드 제거됨

        totalReceipt += teamMonthlyReceipt;
        totalExpense += teamMonthlyExpense;
      });

      // 미지정 어시스트 처리
      const allCacheAssists = new Set(Object.keys(monthlyTotalCache));
      const unprocessedAssists = Array.from(allCacheAssists).filter(assistName => !processedAssists.has(assistName));
      
      if (unprocessedAssists.length > 0) {
        let unprocessedReceiptTotal = 0;
        let unprocessedExpenseTotal = 0;
        const unassignedEmployees: string[] = [];
        const regularUnassigned: string[] = [];
        
        unprocessedAssists.forEach(assistName => {
          const assistMonthlyReceipt = monthlyTotalCache[assistName] || 0;
          const assistMonthlyExpense = monthlyExpenseCache[assistName] || 0;
          
          // 직원 데이터 확인
          const matchedEmployee = this.findMatchingEmployee(assistName, employees);
          if (!matchedEmployee && assistMonthlyReceipt > 0) {
            unassignedEmployees.push(assistName);
          } else {
            regularUnassigned.push(assistName);
          }
          
          unprocessedReceiptTotal += assistMonthlyReceipt;
          unprocessedExpenseTotal += assistMonthlyExpense;
        });

        if (unprocessedReceiptTotal > 0 || unprocessedExpenseTotal > 0) {
          // 미지정 팀 헤더 생성 코드 제거됨

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          let unassignedTotalReceipt = 0;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          let unassignedTotalExpense = 0;

          // 직원 데이터 없는 미지정 직원들 개별 행 추가
          unassignedEmployees.forEach(assistName => {
            const assistMonthlyReceipt = monthlyTotalCache[assistName] || 0;
            const assistMonthlyExpense = monthlyExpenseCache[assistName] || 0;
            
            data.push({
              team: '미지정',
              teamName: '',
              assistName: assistName,
              count: 0,
              amount: 0,
              monthlyReceipt: assistMonthlyReceipt,
              monthlyExpense: assistMonthlyExpense,
              monthlyProfit: assistMonthlyReceipt - assistMonthlyExpense,
              receiptTotal: assistMonthlyReceipt,
              expenseTotal: assistMonthlyExpense,
              isAssistRow: true,
              isUnassignedEmployee: true
            });
            
            unassignedTotalReceipt += assistMonthlyReceipt;
            unassignedTotalExpense += assistMonthlyExpense;
          });
          
          // 일반 미지정 어시스트들 개별 행 추가
          regularUnassigned.forEach(assistName => {
            const assistMonthlyReceipt = monthlyTotalCache[assistName] || 0;
            const assistMonthlyExpense = monthlyExpenseCache[assistName] || 0;
            
            data.push({
              team: '미지정',
              teamName: '',
              assistName: assistName,
              count: 0,
              amount: 0,
              monthlyReceipt: assistMonthlyReceipt,
              monthlyExpense: assistMonthlyExpense,
              monthlyProfit: assistMonthlyReceipt - assistMonthlyExpense,
              receiptTotal: assistMonthlyReceipt,
              expenseTotal: assistMonthlyExpense,
              isAssistRow: true,
              isUnassignedEmployee: false
            });
            
            unassignedTotalReceipt += assistMonthlyReceipt;
            unassignedTotalExpense += assistMonthlyExpense;
          });

          // 미지정 팀 헤더 업데이트 코드 제거됨

          // 미지정 (직원 데이터 없음) 별도 헤더 추가 (필요한 경우)
          if (unassignedEmployees.length > 0) {
            const unassignedEmployeeReceipt = unassignedEmployees.reduce((sum, name) => sum + (monthlyTotalCache[name] || 0), 0);
            const unassignedEmployeeExpense = unassignedEmployees.reduce((sum, name) => sum + (monthlyExpenseCache[name] || 0), 0);
            
            data.push({
              team: '미지정 (직원 데이터 없음)',
              teamName: '미지정 (직원 데이터 없음)',
              assistName: '',
              count: 0,
              amount: 0,
              monthlyReceipt: 0,
              monthlyExpense: 0,
              monthlyProfit: 0,
              receiptTotal: 0,
              expenseTotal: 0,
              // isTeamRow 제거됨
            });

            // 미지정 어시스트 개별 행 추가
            data.push({
              team: '미지정 (직원 데이터 없음)',
              teamName: '',
              assistName: '미지정',
              count: 0,
              amount: 0,
              monthlyReceipt: unassignedEmployeeReceipt,
              monthlyExpense: unassignedEmployeeExpense,
              monthlyProfit: unassignedEmployeeReceipt - unassignedEmployeeExpense,
              receiptTotal: unassignedEmployeeReceipt,
              expenseTotal: unassignedEmployeeExpense,
              isAssistRow: true,
              isUnassignedEmployee: true
            });

            // 헤더 업데이트 코드 제거됨
          }
        }
      }

      return data;
    } catch (error) {
      console.error(`[Excel 배열형] ${dateStr} 계산 실패:`, error);
      return [];
    }
  }

  /**
   * 팀별 아이템 그룹핑 (성능 최적화)
   */
  private groupItemsByTeam(assistItems: AssistReceiptItem[], employees: Employee[]): Record<string, AssistReceiptItem[]> {
    const itemsByTeam: Record<string, AssistReceiptItem[]> = {};
    
    for (const item of assistItems) {
      const teamName = this.mapItemToTeamCached(item, employees);
      
      if (!itemsByTeam[teamName]) {
        itemsByTeam[teamName] = [];
      }
      itemsByTeam[teamName].push(item);
    }
    
    return itemsByTeam;
  }

  /**
   * 캐시된 팀 매핑 (성능 향상)
   */
  private mapItemToTeamCached(item: AssistReceiptItem, employees: Employee[]): string {
    const cacheKey = `${item.assistName}_${employees.length}`;
    
    if (this.employeeMatchCache.has(cacheKey)) {
      const cached = this.employeeMatchCache.get(cacheKey)!;
      return cached.employee?.team || '미지정';
    }
    
    const matchResult = this.findMatchingEmployeeEnhanced(item.assistName, employees);
    const teamName = matchResult.employee?.team || '미지정';
    
    // 결과 캐시에 저장
    this.employeeMatchCache.set(cacheKey, matchResult);
    
    return teamName;
  }

  /**
   * 팀별 데이터 집계 (벌크 처리)
   */
  private aggregateTeamData(teamData: any, items: AssistReceiptItem[]): void {
    for (const item of items) {
      teamData.dailyCount += item.count || 0;
      teamData.dailyAmount += item.amount || 0;
      
      if ('isCash' in item && item.isCash) {
        teamData.dailyCash += item.amount || 0;
      } else {
        teamData.dailyCard += item.amount || 0;
      }

      // 어시스트별 통계 (기존 로직 유지)
      if (!teamData.assistantStats[item.assistName]) {
        teamData.assistantStats[item.assistName] = {
          count: 0,
          amount: 0,
          cashAmount: 0,
          cardAmount: 0
        };
      }
      
      const stats = teamData.assistantStats[item.assistName];
      stats.count += item.count || 0;
      stats.amount += item.amount || 0;
      
      if ('isCash' in item && item.isCash) {
        stats.cashAmount += item.amount || 0;
      } else {
        stats.cardAmount += item.amount || 0;
      }
    }
  }

  /**
   * 최적화된 월별 데이터 추가
   */
  private addOptimizedMonthlyData(
    teamCollectionMap: Record<string, any>,
    assistExpenseCache: Record<string, number>,
    employees: Employee[]
  ): void {
    // 월별 수납액 추가 (캐시 활용)
    Object.entries(this.teamReceiptCache).forEach(([teamName, total]) => {
      if (teamCollectionMap[teamName]) {
        teamCollectionMap[teamName].receiptTotal = total;
      }
    });

    // 월별 지출액 추가 (사전 계산된 캐시 활용)
    Object.entries(teamCollectionMap).forEach(([teamName, teamData]) => {
      let teamExpenseTotal = 0;

      // 해당 팀의 어시스트들 수집
      Object.keys(teamData.assistantStats || {}).forEach(assistName => {
        const assistExpense = assistExpenseCache[assistName] || 0;
        teamExpenseTotal += assistExpense;
      });

      teamData.expenseTotal = teamExpenseTotal;
    });
  }

  /**
   * 직원 매칭 사전 처리 (백그라운드)
   */
  private async preProcessEmployeeMatching(assistItems: AssistReceiptItem[], employees: Employee[]): Promise<void> {
    const uniqueAssistNames = Array.from(new Set(assistItems.map(item => item.assistName)));
    
    // 백그라운드에서 모든 어시스트 이름에 대해 매칭 사전 처리
    for (const assistName of uniqueAssistNames) {
      this.findMatchingEmployeeEnhanced(assistName, employees);
    }
    
  }

  /**
   * 수동 저장용 팀별 수납 현황 계산
   */
  async calculateForManualSave(
    assistItems: AssistReceiptItem[],
    employees: Employee[],
    budgetData: any,
    assistCashierDoctorData?: AssistCashierDoctorData,
    yearMonth?: string
  ): Promise<any> {
    const input: TeamCollectionInput = {
      assistItems,
      employees,
      budgetData,
      assistCashierDoctorData,
      yearMonth
    };

    const { teamCollection } = await this.calculateForDisplay(input);
    
    // Firebase 저장 형식으로 변환
    const result: Record<string, any> = {};
    
    Object.entries(teamCollection).forEach(([teamName, data]) => {
      result[teamName] = {
        team: teamName,
        count: data.dailyCount,
        amount: data.dailyAmount,
        cashAmount: data.dailyCash,
        cardAmount: data.dailyCard,
        receiptTotal: data.receiptTotal || 0,
        expenseTotal: data.expenseTotal || 0,
        assistantStats: data.assistantStats || {},
        doctorStats: data.doctorStats || {}
      };
    });

    return result;
  }

  /**
   * 아이템을 팀으로 매핑 (화면 표시용 - 진료의사 매핑 포함 5단계 매핑)
   */
  private async mapItemToTeam(
    item: AssistReceiptItem,
    employees: Employee[],
    assistCashierDoctorData?: AssistCashierDoctorData,
    budgetData?: any,
    yearMonth?: string
  ): Promise<string> {
    const assistName = item.assistName || '미지정';
    const cashierName = item.cashierName || '';

    // 🆕 0단계: assistTeam 임시직 배정 우선 처리 (최고 우선순위)
    if ('assistTeam' in item && item.assistTeam && item.assistTeam.trim()) {
      return item.assistTeam.trim();
    }

    // 🆕 1단계: assistCashierDoctorData 사용 (진료의사 매핑 데이터 우선 적용)
    // 명시적 doctorTeamMapping 판정이 퍼지 직원 매칭보다 먼저 실행되어야 함
    if (assistCashierDoctorData && assistName && cashierName) {
      const key = `${assistName}_${cashierName}`;
      const mappingData = assistCashierDoctorData[key];

      // (1-1 '옛 의사 월 매핑으로 팀 박기'와 1-2 '진료의사 → 직원 팀 박기' 모두 제거했다.
      //  업로드 시점에 어시스트 없는 행에 팀을 박으면 그 수납건이 귀속 정본(salesAttribution)의 풀에서
      //  빠져 "그날 그 의사의 어시스트 있는 진료 팀 비율" 규칙과 관리자 지정이 무시된다.
      //  진료의사로 팀을 정하는 일은 집계 시점의 정본 규칙 소관이다.)
      void mappingData;
    }

    // 2단계: 어시스트 이름으로 직접 매칭
    const matchedEmployee = this.findMatchingEmployeeConfident(assistName, employees);
    if (matchedEmployee?.team) {
      return matchedEmployee.team;
    }

    // 3단계: 수납자 이름으로 매칭
    if (cashierName) {
      const cashierEmployee = this.findMatchingEmployeeConfident(cashierName, employees);
      if (cashierEmployee?.team) {
        return cashierEmployee.team;
      }
    }

    // (4단계였던 '진료의사로 팀 박아 넣기'는 제거했다 — 옛 의사 월 매핑(4-1)과 의사-직원 매칭(4-2) 모두.
    //  업로드 시점에 어시스트 없는 행에 팀을 박으면 그 수납건이 귀속 정본(salesAttribution)의 풀에서 빠져
    //  "그날 그 의사의 어시스트 있는 진료 팀 비율" 규칙과 관리자 지정이 무시된다. 6·7월 저장 행의
    //  team='1팀'은 바로 이 경로가 남긴 것이다. 이제 빈 행은 '미지정'으로 남고 집계 시점에 귀속된다.)
    return '미지정';
  }

  /**
   * 아이템을 팀으로 매핑 (Excel용 - 간단한 매핑)
   */
  private async mapItemToTeamForExcel(
    item: AssistReceiptItem,
    employees: Employee[]
  ): Promise<string> {
    const assistName = item.assistName || '미지정';
    
    // 어시스트 이름으로 직접 매칭
    const matchedEmployee = employees.find(emp => 
      emp.name.toLowerCase().trim() === assistName.toLowerCase().trim()
    );
    
    return matchedEmployee?.team || '미지정';
  }

  /**
   * 어시스트 이름과 일치하는 직원을 찾는 함수 (향상된 버전)
   */
  private findMatchingEmployeeEnhanced(assistName: string, employees: Employee[]): EmployeeMatchResult {
    // 개선된 캐시 키 생성 (직원 이름들의 해시를 포함)
    const employeeNameHash = employees.map(emp => emp.name).sort().join(',').slice(0, 50);
    const cacheKey = `${assistName}_${employees.length}_${employeeNameHash}`;
    
    if (this.employeeMatchCache.has(cacheKey)) {
      return this.employeeMatchCache.get(cacheKey)!;
    }

    const result = this._performMatching(assistName, employees);
    this.employeeMatchCache.set(cacheKey, result);
    
    // 캐시 크기 제한 (메모리 누수 방지)
    if (this.employeeMatchCache.size > 1000) {
      const firstKey = this.employeeMatchCache.keys().next().value;
      this.employeeMatchCache.delete(firstKey);
    }
    
    return result;
  }

  /**
   * 기존 findMatchingEmployee 메서드 (호환성 유지)
   */
  public findMatchingEmployee(assistName: string, employees: any[]): any | null {
    const result = this.findMatchingEmployeeEnhanced(assistName, employees);
    return result.employee;
  }

  /**
   * 돈 귀속 경로 전용 직원 매칭 — 성씨 추정(confidence 0.5) 등 저신뢰 매칭은
   * 채택하지 않아 매칭 실패가 '미지정'으로 남아 감지 가드에 노출되게 한다
   */
  private findMatchingEmployeeConfident(name: string, employees: Employee[]): Employee | null {
    const result = this.findMatchingEmployeeEnhanced(name, employees);
    return result.employee && result.confidence >= 0.7 ? result.employee : null;
  }

  /**
   * 실제 매칭 수행
   */
  private _performMatching(assistName: string, employees: Employee[]): EmployeeMatchResult {
    // 빈 이름 체크
    if (!assistName || !assistName.trim()) {
      return { employee: null, matchType: 'none', confidence: 0 };
    }

    // 직원 데이터 체크
    if (!employees || employees.length === 0) {
      return { employee: null, matchType: 'none', confidence: 0 };
    }

    const cleanAssistName = assistName.trim();

    // 1. 정확한 이름 매칭 (대소문자 무시)
    let matched = employees.find(emp =>
      emp.name.toLowerCase() === cleanAssistName.toLowerCase()
    );
    if (matched) {
      return { employee: matched, matchType: 'exact', confidence: 1.0 };
    }

    // 2. 공백 제거 후 매칭
    const assistNameNoSpace = cleanAssistName.replace(/\s+/g, '');

    matched = employees.find(emp => {
      const empNameNoSpace = emp.name.replace(/\s+/g, '');
      return empNameNoSpace.toLowerCase() === assistNameNoSpace.toLowerCase();
    });
    if (matched) {
      return { employee: matched, matchType: 'whitespace', confidence: 0.9 };
    }

    // 3. 부분 이름 매칭
    matched = employees.find(emp => {
      const empNameClean = emp.name.replace(/\s+/g, '').toLowerCase();
      const assistNameClean = assistNameNoSpace.toLowerCase();

      // 어시스트 이름이 직원 이름에 포함
      const assistInEmp = empNameClean.includes(assistNameClean) && assistNameClean.length >= TEAM_MAPPING_CONFIG.minNameLength;

      // 직원 이름이 어시스트 이름에 포함
      const empInAssist = assistNameClean.includes(empNameClean) && empNameClean.length >= TEAM_MAPPING_CONFIG.minNameLength;

      return assistInEmp || empInAssist;
    });
    if (matched) {
      return { employee: matched, matchType: 'partial', confidence: 0.7 };
    }

    // 4. 한글 성씨 매칭
    if (cleanAssistName.length >= TEAM_MAPPING_CONFIG.minNameLength) {
      const assistSurname = cleanAssistName.charAt(0);

      matched = employees.find(emp => {
        const empSurname = emp.name.charAt(0);
        const sameLastName = emp.name.length >= TEAM_MAPPING_CONFIG.minNameLength && empSurname === assistSurname;
        const lengthCheck = emp.name.length <= cleanAssistName.length + 2;

        return sameLastName && lengthCheck;
      });
      if (matched) {
        return { employee: matched, matchType: 'surname', confidence: 0.5 };
      }
    }

    return { employee: null, matchType: 'none', confidence: 0 };
  }

  /**
   * 🆕 캐시어 매핑을 포함한 향상된 직원 매칭 함수
   */
  /**
   * 공개 메소드: 아이템의 팀 매핑
   */
  public async mapItemToTeamPublic(
    item: AssistReceiptItem,
    employees: Employee[],
    assistCashierDoctorData?: AssistCashierDoctorData,
    budgetData?: any,
    yearMonth?: string
  ): Promise<string> {
    return this.mapItemToTeam(item, employees, assistCashierDoctorData, budgetData, yearMonth);
  }

  public findMatchingEmployeeWithCashier(
    assistName: string, 
    cashierName: string | undefined, 
    employees: Employee[]
  ): Employee | null {
    // 1단계: 어시스트명으로 직접 매칭
    const assistMatch = this.findMatchingEmployee(assistName, employees);
    if (assistMatch) {
      return assistMatch;
    }

    // 2단계: 캐시어명으로 매칭 (fallback)
    if (cashierName && cashierName.trim()) {
      const cashierMatch = this.findMatchingEmployee(cashierName, employees);
      if (cashierMatch) {
        return cashierMatch;
      }
    }

    return null;
  }

  /**
   * 월별 데이터 로드 (스마트 캐싱)
   * @param yearMonth 년월 (YYYY-MM 형식)
   * @param employees 외부 직원 목록 (budgetData 기반, Firebase employees 컬렉션에 없는 직원 포함)
   */
  public async loadMonthlyData(yearMonth: string, employees?: Employee[]): Promise<MonthlyDataResult> {
    const cacheKey = yearMonth;
    const now = Date.now();
    this.performanceStats.totalRequests++;
    
    // 캐시 확인 (타임스탬프 포함)
    if (this.monthlyDataCache.has(cacheKey)) {
      const cached = this.monthlyDataCache.get(cacheKey)!;
      if (now - cached.timestamp < this.CACHE_DURATION) {
        this.performanceStats.cacheHits++;
        return { receipt: cached.receipt, expense: cached.expense, isFromCache: true };
      }
    }

    try {
      this.performanceStats.cacheMisses++;

      // 병렬 로드로 성능 향상
      // employees 파라미터 전달: budgetData 기반 직원 목록으로 Firebase employees 컬렉션에 없는 직원도 팀 매핑 가능
      const [receipt, expense] = await Promise.all([
        getMonthlyReceiptDataByTeam(yearMonth, employees),
        getMonthlyExpenseDataByTeam(yearMonth)
      ]);

      const data = { receipt, expense, timestamp: now };
      this.monthlyDataCache.set(cacheKey, data);

      // 어시스트별 캐시도 미리 계산하여 저장
      this.preCalculateAssistCaches(yearMonth, receipt, expense);

      return { receipt, expense, isFromCache: false };
    } catch (error) {
      console.error(`[월별 데이터] 로드 실패: ${yearMonth}`, error);
      return this.handleMonthlyDataError(error, '월별 수납 데이터 로드') as MonthlyDataResult;
    }
  }

  /**
   * 어시스트별 월 캐시 미리 계산
   */
  private preCalculateAssistCaches(yearMonth: string, receiptData: any, expenseData: any): void {
    const assistReceiptCache: Record<string, number> = {};
    const assistExpenseCache: Record<string, number> = {};

    // 수납 데이터 사전 집계
    Object.entries(receiptData).forEach(([teamName, receipts]) => {
      if (Array.isArray(receipts)) {
        receipts.forEach((item: any) => {
          const assistName = item.assistName || '미지정';
          const amount = item.amount || 0;

          assistReceiptCache[assistName] = (assistReceiptCache[assistName] || 0) + amount;
        });
      }
    });

    // 지출 데이터 사전 집계
    Object.entries(expenseData).forEach(([teamName, expenses]) => {
      if (Array.isArray(expenses)) {
        expenses.forEach((item: any) => {
          const assistName = item.assistName || '미지정';
          const amount = item.amount || 0;
          assistExpenseCache[assistName] = (assistExpenseCache[assistName] || 0) + amount;
        });
      }
    });

    // 캐시 저장
    this.monthlyAssistCache.set(yearMonth, assistReceiptCache);
    this.monthlyExpenseCache.set(yearMonth, assistExpenseCache);
  }

  /**
   * 어시스트별 월 수납액 계산 (캐시 활용)
   */
  private calculateAssistReceiptCache(monthlyReceiptData: any, yearMonth?: string): void {
    // 사전 계산된 캐시가 있으면 사용
    if (yearMonth && this.monthlyAssistCache.has(yearMonth)) {
      this.assistReceiptCache = { ...this.monthlyAssistCache.get(yearMonth)! };

      // 팀별 수납액도 계산
      this.teamReceiptCache = {};
      Object.entries(monthlyReceiptData).forEach(([teamName, receipts]) => {
        if (Array.isArray(receipts)) {
          const teamTotal = receipts.reduce((sum: number, item: any) =>
            sum + (item.amount || 0), 0
          );
          this.teamReceiptCache[teamName] = teamTotal;
        }
      });
      return;
    }

    // 기존 계산 로직 (fallback)
    this.assistReceiptCache = {};
    this.teamReceiptCache = {};

    Object.entries(monthlyReceiptData).forEach(([teamName, receipts]) => {
      if (Array.isArray(receipts)) {
        let teamTotal = 0;

        receipts.forEach((item: any) => {
          const assistName = item.assistName || '미지정';
          const amount = item.amount || 0;
          this.assistReceiptCache[assistName] = (this.assistReceiptCache[assistName] || 0) + amount;
          teamTotal += amount;
        });
        this.teamReceiptCache[teamName] = teamTotal;
      }
    });
  }

  /**
   * 월별 지출액 계산 (캐시 활용)
   */
  private calculateAssistExpenseCache(monthlyExpenseData: any, yearMonth?: string): Record<string, number> {
    // 사전 계산된 캐시가 있으면 사용
    if (yearMonth && this.monthlyExpenseCache.has(yearMonth)) {
      return { ...this.monthlyExpenseCache.get(yearMonth)! };
    }

    // 기존 계산 로직 (fallback)
    const assistExpenseCache: Record<string, number> = {};

    Object.entries(monthlyExpenseData).forEach(([teamName, expenses]) => {
      if (Array.isArray(expenses)) {
        expenses.forEach((item: any) => {
          const assistName = item.assistName || '미지정';
          const amount = item.amount || 0;
          assistExpenseCache[assistName] = (assistExpenseCache[assistName] || 0) + amount;
        });
      }
    });

    return assistExpenseCache;
  }

  /**
   * 팀별 월별 데이터 추가
   */
  private addMonthlyDataToTeams(
    teamCollectionMap: Record<string, any>,
    monthlyData: { receipt: any; expense: any },
    employees: Employee[]
  ): void {
    // 월별 수납액 추가
    Object.entries(this.teamReceiptCache).forEach(([teamName, total]) => {
      if (teamCollectionMap[teamName]) {
        teamCollectionMap[teamName].receiptTotal = total;
      }
    });

    // 월별 지출액 추가
    Object.entries(monthlyData.expense).forEach(([teamName, expenses]) => {
      if (teamCollectionMap[teamName] && Array.isArray(expenses)) {
        const total = expenses.reduce((sum: number, item: any) => 
          sum + (item.amount || 0), 0
        );
        teamCollectionMap[teamName].expenseTotal = total;
      }
    });
  }

  /**
   * 캐시된 어시스트 수납액 반환
   */
  getAssistReceiptCache(yearMonth: string): Record<string, number> {
    if (this.monthlyAssistCache.has(yearMonth)) {
      return { ...this.monthlyAssistCache.get(yearMonth)! };
    }
    return { ...this.assistReceiptCache };
  }

  /**
   * 캐시된 어시스트 지출액 반환
   */
  getAssistExpenseCache(yearMonth: string): Record<string, number> {
    if (this.monthlyExpenseCache.has(yearMonth)) {
      return { ...this.monthlyExpenseCache.get(yearMonth)! };
    }
    // 지출액 캐시 계산이 안 된 경우를 위한 fallback
    return {};
  }

  /**
   * 최적화된 화면 표시용 계산 (캐싱 활용)
   */
  async calculateForDisplayOptimized(input: TeamCollectionInput & { yearMonth?: string }): Promise<TeamCollectionOutput> {
    const { yearMonth, employees, ...baseInput } = input;

    // 월별 캐시 사전 로드
    // employees 전달: budgetData 기반 직원 목록으로 Firebase에 없는 직원도 팀 매핑 가능
    if (yearMonth) {
      await this.loadMonthlyData(yearMonth, employees);
    }

    // 기존 계산 로직 실행 (내부적으로 캐시 활용)
    return this.calculateForDisplay({ ...baseInput, employees });
  }

  /**
   * 성능 모니터링을 위한 캐시 통계
   */
  getCacheStats(): {
    monthlyDataCacheSize: number;
    assistCacheSize: number;
    expenseCacheSize: number;
    employeeMatchCacheSize: number;
    cacheHitRate: number;
  } {
    const hitRate = this.performanceStats.totalRequests > 0
      ? (this.performanceStats.cacheHits / this.performanceStats.totalRequests) * 100
      : 0;
      
    return {
      monthlyDataCacheSize: this.monthlyDataCache.size,
      assistCacheSize: this.monthlyAssistCache.size,
      expenseCacheSize: this.monthlyExpenseCache.size,
      employeeMatchCacheSize: this.employeeMatchCache.size,
      cacheHitRate: hitRate
    };
  }

  /**
   * 스마트 캐시 무효화 (필요한 부분만)
   */
  invalidateCache(options: {
    yearMonth?: string;
    employeeData?: boolean;
    monthlyData?: boolean;
  } = {}): void {
    const { yearMonth, employeeData = false, monthlyData = false } = options;

    if (employeeData) {
      this.employeeMatchCache.clear();
    }

    if (monthlyData && yearMonth) {
      this.monthlyDataCache.delete(yearMonth);
      this.monthlyAssistCache.delete(yearMonth);
      this.monthlyExpenseCache.delete(yearMonth);
    } else if (monthlyData) {
      this.monthlyDataCache.clear();
      this.monthlyAssistCache.clear();
      this.monthlyExpenseCache.clear();
    }
  }

  /**
   * 월 수납액 계산 검증 메서드
   */
  validateMonthlyReceiptCalculation(yearMonth: string): {
    isValid: boolean;
    issues: string[];
    summary: any;
  } {
    const issues: string[] = [];
    let isValid = true;

    // 1. 캐시 일관성 검증
    const assistCache = this.monthlyAssistCache.get(yearMonth);
    const teamCache = this.teamReceiptCache;

    if (!assistCache) {
      issues.push(`어시스트별 캐시가 없음 (${yearMonth})`);
      isValid = false;
    }

    if (Object.keys(teamCache).length === 0) {
      issues.push('팀별 캐시가 비어있음');
      isValid = false;
    }

    // 2. 합계 검증
    if (assistCache) {
      const assistTotal = Object.values(assistCache).reduce((sum, amount) => sum + amount, 0);
      const teamTotal = Object.values(teamCache).reduce((sum, amount) => sum + amount, 0);

      const difference = Math.abs(assistTotal - teamTotal);
      const tolerance = assistTotal * 0.01; // 1% 허용 오차

      if (difference > tolerance) {
        issues.push(`어시스트별 합계(${assistTotal.toLocaleString()})와 팀별 합계(${teamTotal.toLocaleString()}) 불일치 (차이: ${difference.toLocaleString()}원)`);
        isValid = false;
      }
    }

    // 3. 중복 어시스트 검증
    const duplicateCheck = new Map<string, string[]>();
    Object.entries(teamCache).forEach(([teamName, amount]) => {
      if (assistCache) {
        Object.entries(assistCache).forEach(([assistName, assistAmount]) => {
          if (!duplicateCheck.has(assistName)) {
            duplicateCheck.set(assistName, []);
          }
          duplicateCheck.get(assistName)!.push(teamName);
        });
      }
    });

    const duplicates = Array.from(duplicateCheck.entries()).filter(([, teams]) => teams.length > 1);
    if (duplicates.length > 0) {
      duplicates.forEach(([assistName, teams]) => {
        issues.push(`${assistName} 어시스트가 여러 팀에 매핑됨: ${teams.join(', ')}`);
      });
      isValid = false;
    }

    const summary = {
      yearMonth,
      assistCacheSize: assistCache ? Object.keys(assistCache).length : 0,
      teamCacheSize: Object.keys(teamCache).length,
      assistTotal: assistCache ? Object.values(assistCache).reduce((sum, amount) => sum + amount, 0) : 0,
      teamTotal: Object.values(teamCache).reduce((sum, amount) => sum + amount, 0),
      duplicateCount: duplicates.length,
      cacheStats: this.getCacheStats()
    };

    return { isValid, issues, summary };
  }

  /**
   * 캐시 초기화
   */
  clearCache(): void {
    this.assistReceiptCache = {};
    this.teamReceiptCache = {};
    this.monthlyDataCache.clear();
    this.monthlyAssistCache.clear();
    this.monthlyExpenseCache.clear();
    this.employeeMatchCache.clear();
    this.performanceStats = {
      cacheHits: 0,
      cacheMisses: 0,
      totalRequests: 0
    };
  }

  /**
   * 월별 데이터 로드 에러 처리
   */
  private handleMonthlyDataError(error: any, context: string): MonthlyDataResult {
    console.error(`[TeamCollectionService] ${context} 에러:`, error);
    return { receipt: {}, expense: {}, isFromCache: false };
  }

  /**
   * 팀 매핑 에러 처리
   */
  private handleTeamMappingError(error: any, assistName: string): string {
    console.warn(`[TeamCollectionService] 팀 매핑 실패 (${assistName}):`, error);
    return '미지정';
  }

  /**
   * 🆕 일별 팀 활동 추적 - 날짜_어시스트명_팀명 형태의 활동 키 기록
   */
  trackDailyTeamActivity(date: string, assistName: string, teamName: string): string {
    const activityKey = `${date}_${assistName}_${teamName}`;

    if (!this.dailyTeamActivityCache.has(date)) {
      this.dailyTeamActivityCache.set(date, new Map());
    }

    const dateCache = this.dailyTeamActivityCache.get(date)!;
    if (!dateCache.has(assistName)) {
      dateCache.set(assistName, new Set());
    }

    dateCache.get(assistName)!.add(teamName);

    return activityKey;
  }

  /**
   * 🆕 특정 날짜의 어시스트별 팀 활동 조회
   */
  getDailyTeamActivities(date: string): Map<string, Set<string>> {
    return this.dailyTeamActivityCache.get(date) || new Map();
  }

  /**
   * 🆕 여러 팀에서 활동하는 어시스트 조회
   */
  getMultiTeamAssists(date: string): Array<{assistName: string, teams: string[], teamCount: number}> {
    const dailyActivities = this.getDailyTeamActivities(date);
    const multiTeamAssists: Array<{assistName: string, teams: string[], teamCount: number}> = [];
    
    dailyActivities.forEach((teams, assistName) => {
      if (teams.size > 1) {
        multiTeamAssists.push({
          assistName,
          teams: Array.from(teams),
          teamCount: teams.size
        });
      }
    });
    
    return multiTeamAssists;
  }

  /**
   * 🆕 최희정과 같은 미지정 직원의 팀별 분리 로그 출력
   */
  logTeamSeparationDetails(date: string, assistName: string): void {
    // 팀별 분리 상세 정보 조회용 (로그 없이 데이터만 반환 가능)
    const dailyActivities = this.getDailyTeamActivities(date);
    dailyActivities.get(assistName);
  }

  /**
   * 🆕 일별 팀 활동 캐시 정리 (메모리 관리)
   */
  clearDailyActivityCache(beforeDate?: string): void {
    if (beforeDate) {
      // 특정 날짜 이전 데이터만 삭제
      const cutoffDate = new Date(beforeDate);
      cutoffDate.setDate(cutoffDate.getDate() - 30); // 30일 이전 데이터 삭제
      
      this.dailyTeamActivityCache.forEach((_, date) => {
        if (new Date(date) < cutoffDate) {
          this.dailyTeamActivityCache.delete(date);
        }
      });
    } else {
      // 전체 캐시 삭제
      this.dailyTeamActivityCache.clear();
    }
  }
}